import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash2, Power } from "lucide-react";
import { PushToProdButton } from "@/components/PushToProdButton";

const EMPTY = {
  code: "",
  discount_type: "percent",
  discount_value: 10,
  min_order_usd: 0,
  max_uses: "",
  expires_at: "",
  stacks_with_affiliate: true,
  active: true,
  notes: "",
};

export default function PromoCodes() {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/admin/promo-codes").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    setBusy(true);
    try {
      const payload = {
        ...form,
        code: form.code.trim().toUpperCase(),
        discount_value: Number(form.discount_value),
        min_order_usd: Number(form.min_order_usd || 0),
        max_uses: form.max_uses ? Number(form.max_uses) : null,
        expires_at: form.expires_at
          ? new Date(form.expires_at).toISOString()
          : null,
      };
      await api.post("/admin/promo-codes", payload);
      toast.success(`Promo ${payload.code} created`);
      setShowCreate(false);
      setForm(EMPTY);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to create promo");
    } finally { setBusy(false); }
  };

  const toggleActive = async (p) => {
    try {
      await api.patch(`/admin/promo-codes/${p.id}`, { active: !p.active });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Update failed");
    }
  };

  const remove = async (p) => {
    if (!window.confirm(`Delete promo code ${p.code}? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/promo-codes/${p.id}`);
      toast.success(`Promo ${p.code} deleted`);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed");
    }
  };

  return (
    <div className="max-w-screen-2xl mx-auto px-6 py-10">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <div className="label-eyebrow">Promotions</div>
          <h1 className="text-2xl font-bold tracking-tight mt-1">Discount / Promo Codes</h1>
          <p className="text-sm text-[#5E636E] mt-2 max-w-2xl">
            Create marketing discount codes. Each code can be percent or fixed-amount, gated by minimum order, capped by usage limit, and optionally stackable with affiliate codes.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          className="btn-primary inline-flex items-center gap-2"
          style={{ background: "#003366" }}
          data-testid="new-promo-btn"
        >
          <Plus className="h-4 w-4" /> New promo code
        </button>
      </div>

      <div className="mt-6 border border-[#D1D5DB] overflow-x-auto">
        <table className="w-full data-table">
          <thead>
            <tr>
              <th className="text-left px-4 py-2">Code</th>
              <th className="text-left px-4 py-2">Discount</th>
              <th className="text-left px-4 py-2">Min order</th>
              <th className="text-left px-4 py-2">Uses</th>
              <th className="text-left px-4 py-2">Expires</th>
              <th className="text-left px-4 py-2">Stacks w/ Affiliate</th>
              <th className="text-left px-4 py-2">Status</th>
              <th className="text-right px-4 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-4 py-10 text-center text-sm text-[#5E636E]">No promo codes yet. Create your first one to run a campaign.</td></tr>
            )}
            {items.map((p) => (
              <tr key={p.id} className="border-t border-[#D1D5DB]" data-testid={`promo-row-${p.code}`}>
                <td className="px-4 py-2 font-mono text-sm font-semibold">{p.code}</td>
                <td className="px-4 py-2 font-mono text-xs">
                  {p.discount_type === "fixed" ? `$${Number(p.discount_value).toFixed(2)}` : `${p.discount_value}%`}
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  {p.min_order_usd > 0 ? `$${Number(p.min_order_usd).toFixed(2)}` : "—"}
                </td>
                <td className="px-4 py-2 font-mono text-xs">
                  {p.uses || 0}{p.max_uses ? ` / ${p.max_uses}` : ""}
                </td>
                <td className="px-4 py-2 font-mono text-[11px] text-[#5E636E]">
                  {p.expires_at ? new Date(p.expires_at).toLocaleDateString() : "Never"}
                </td>
                <td className="px-4 py-2 text-xs">
                  {p.stacks_with_affiliate ? (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[#E8F5EC] text-[#0B6623] border border-[#0B6623] text-[10px] font-mono uppercase tracking-wider">Yes</span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[#FBEAEA] text-[#A30414] border border-[#A30414] text-[10px] font-mono uppercase tracking-wider">No</span>
                  )}
                </td>
                <td className="px-4 py-2 text-xs">
                  {p.active ? (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[#E8F5EC] text-[#0B6623] border border-[#0B6623] text-[10px] font-mono uppercase tracking-wider">Active</span>
                  ) : (
                    <span className="inline-block px-2 py-0.5 rounded-full bg-[#F7F7F8] text-[#5E636E] border border-[#D1D5DB] text-[10px] font-mono uppercase tracking-wider">Paused</span>
                  )}
                </td>
                <td className="px-4 py-2 text-right">
                  <button
                    onClick={() => toggleActive(p)}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#D1D5DB] rounded text-[11px] font-mono uppercase tracking-wider hover:border-[#003366] mr-2"
                    data-testid={`promo-toggle-${p.code}`}
                    title={p.active ? "Pause" : "Activate"}
                  >
                    <Power className="h-3 w-3" /> {p.active ? "Pause" : "Activate"}
                  </button>
                  <span className="mr-2 inline-block align-middle">
                    <PushToProdButton
                      endpoint={`/admin/promo-codes/${p.id}/push-to-production`}
                      label={p.code}
                      entity="promo code"
                      testid={`promo-push-${p.code}`}
                      showOverride={false}
                    />
                  </span>
                  <button
                    onClick={() => remove(p)}
                    className="inline-flex items-center gap-1 px-2 py-1 border border-[#D1D5DB] rounded text-[11px] font-mono uppercase tracking-wider text-[#A30414] hover:border-[#A30414]"
                    data-testid={`promo-delete-${p.code}`}
                  >
                    <Trash2 className="h-3 w-3" /> Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showCreate && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setShowCreate(false)}>
          <div className="bg-white border border-[#D1D5DB] w-full max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="px-5 py-4 border-b border-[#D1D5DB] flex items-center justify-between">
              <h2 className="text-lg font-bold">New promo code</h2>
              <button onClick={() => setShowCreate(false)} className="text-sm text-[#5E636E] hover:text-[#111216]">✕</button>
            </div>
            <div className="p-5 space-y-4">
              <F label="Code (letters, digits, - or _)">
                <input
                  value={form.code}
                  onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
                  placeholder="e.g. WELCOME15"
                  className="w-full px-3 py-2 border border-[#D1D5DB] font-mono text-sm rounded"
                  data-testid="promo-form-code"
                />
              </F>
              <div className="grid grid-cols-2 gap-3">
                <F label="Discount type">
                  <select
                    value={form.discount_type}
                    onChange={(e) => setForm({ ...form, discount_type: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D1D5DB] text-sm rounded"
                    data-testid="promo-form-type"
                  >
                    <option value="percent">Percent (%)</option>
                    <option value="fixed">Fixed ($)</option>
                  </select>
                </F>
                <F label={form.discount_type === "fixed" ? "Amount ($)" : "Percent (%)"}>
                  <input
                    type="number" min="0" step="0.01"
                    value={form.discount_value}
                    onChange={(e) => setForm({ ...form, discount_value: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D1D5DB] font-mono text-sm rounded"
                    data-testid="promo-form-value"
                  />
                </F>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <F label="Min order ($)">
                  <input
                    type="number" min="0" step="0.01"
                    value={form.min_order_usd}
                    onChange={(e) => setForm({ ...form, min_order_usd: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D1D5DB] font-mono text-sm rounded"
                    data-testid="promo-form-min-order"
                  />
                </F>
                <F label="Max uses (blank = unlimited)">
                  <input
                    type="number" min="1" step="1"
                    value={form.max_uses}
                    onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                    className="w-full px-3 py-2 border border-[#D1D5DB] font-mono text-sm rounded"
                    data-testid="promo-form-max-uses"
                  />
                </F>
              </div>
              <F label="Expires (optional)">
                <input
                  type="datetime-local"
                  value={form.expires_at}
                  onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
                  className="w-full px-3 py-2 border border-[#D1D5DB] text-sm rounded"
                  data-testid="promo-form-expires"
                />
              </F>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.stacks_with_affiliate}
                  onChange={(e) => setForm({ ...form, stacks_with_affiliate: e.target.checked })}
                  className="h-4 w-4 accent-[#003366]"
                  data-testid="promo-form-stacks"
                />
                <span>Allow stacking with affiliate codes</span>
              </label>
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.active}
                  onChange={(e) => setForm({ ...form, active: e.target.checked })}
                  className="h-4 w-4 accent-[#003366]"
                  data-testid="promo-form-active"
                />
                <span>Active immediately</span>
              </label>
              <F label="Internal notes (optional)">
                <input
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="w-full px-3 py-2 border border-[#D1D5DB] text-sm rounded"
                  data-testid="promo-form-notes"
                />
              </F>
            </div>
            <div className="px-5 py-4 border-t border-[#D1D5DB] flex justify-end gap-3">
              <button
                onClick={() => setShowCreate(false)}
                className="px-4 py-2 border border-[#D1D5DB] text-sm rounded"
                data-testid="promo-form-cancel"
              >
                Cancel
              </button>
              <button
                onClick={create}
                disabled={busy || !form.code || !form.discount_value}
                className="btn-primary disabled:opacity-50"
                style={{ background: "#003366" }}
                data-testid="promo-form-submit"
              >
                {busy ? "Creating…" : "Create promo"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function F({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-[#5E636E] block mb-1.5">{label}</label>
      {children}
    </div>
  );
}
